import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, Plus, Minus, Calendar, Clock, User, CheckCircle2, ShoppingBag,
} from "lucide-react";
import { CloudCanvas } from "../components/CloudCanvas";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (n) => `${n.toFixed(2).replace(".", ",")} €`;

const STEPS = [
  { id: 1, label: "Produits", icon: ShoppingBag },
  { id: 2, label: "Date", icon: Calendar },
  { id: 3, label: "Créneau", icon: Clock },
  { id: 4, label: "Infos", icon: User },
  { id: 5, label: "Récap", icon: CheckCircle2 },
];

const Stepper = ({ step }) => (
  <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
    {STEPS.map((s, i) => {
      const Icon = s.icon;
      const active = step === s.id;
      const done = step > s.id;
      return (
        <div key={s.id} className="flex items-center">
          <div
            data-testid={`step-indicator-${s.id}`}
            className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
              active
                ? "bg-coral text-white scale-110 shadow-[0_6px_16px_rgba(229,91,91,0.35)]"
                : done
                ? "bg-amyu-pistachio text-amyu-ink"
                : "bg-amyu-cream text-amyu-muted"
            }`}
          >
            <Icon size={14} />
          </div>
          {i < STEPS.length - 1 && (
            <div className={`w-6 md:w-10 h-[2px] mx-1 ${done ? "bg-amyu-pistachio" : "bg-amyu-cream"}`} />
          )}
        </div>
      );
    })}
  </div>
);

export default function Commander() {
  usePageMeta({
    title: "Commander — Amyu | Click & Collect à Carcassonne",
    description:
      "Commande tes tiramisus artisanaux Amyu en ligne. Choisis ta date et ton créneau de retrait à Carcassonne.",
    canonical: "https://amyu.fr/commander",
  });
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [products, setProducts] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState({});
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [info, setInfo] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([axios.get(`${API}/products`), axios.get(`${API}/availability`)])
      .then(([p, a]) => {
        setProducts(p.data.products);
        setAvailability(a.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const items = useMemo(
    () =>
      products
        .filter((p) => (qty[p.id] || 0) > 0)
        .map((p) => ({ ...p, quantity: qty[p.id], line_total: p.price * qty[p.id] })),
    [products, qty]
  );
  const total = useMemo(() => items.reduce((s, it) => s + it.line_total, 0), [items]);

  const availableDates = useMemo(() => {
    if (!availability) return [];
    const out = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const min = new Date(today);
    min.setDate(min.getDate() + availability.lead_time_days);
    for (let i = 0; i < 42; i++) {
      const d = new Date(min);
      d.setDate(min.getDate() + i);
      const iso = d.toISOString().split("T")[0];
      if (!availability.open_days.includes((d.getDay() + 6) % 7)) continue; // JS 0=Sun→Python 6
      if (availability.closed_dates.includes(iso)) continue;
      out.push({ iso, label: d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) });
    }
    return out;
  }, [availability]);

  const setQuantity = (id, delta) =>
    setQty((q) => {
      const next = Math.max(0, (q[id] || 0) + delta);
      const copy = { ...q };
      if (next === 0) delete copy[id];
      else copy[id] = next;
      return copy;
    });

  // Reset slot when date changes (schedule may differ per weekday)
  useEffect(() => {
    setSlot("");
  }, [date]);

  const canNext = () => {
    if (step === 1) return items.length > 0;
    if (step === 2) return !!date;
    if (step === 3) return !!slot;
    if (step === 4) return info.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.email);
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/orders`, {
        customer_name: info.name.trim(),
        email: info.email.trim(),
        phone: info.phone.trim() || null,
        items: items.map((it) => ({ product_id: it.id, name: it.name, quantity: it.quantity })),
        pickup_date: date,
        pickup_slot: slot,
        notes: info.notes.trim() || null,
      });
      navigate(`/commande/${r.data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-amyu-bg">
        <Loader2 className="animate-spin text-coral" size={32} />
      </div>
    );

  return (
    <div className="min-h-screen bg-amyu-bg relative">
      <CloudCanvas />
      <Header />
      <main className="relative z-10 max-w-3xl mx-auto px-5 py-24 md:py-28 pb-32 md:pb-16">
        <div className="text-center mb-6">
          <span className="text-xs uppercase tracking-[0.24em] text-coral font-medium">Click &amp; Collect</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">
            Ta commande <span className="italic text-coral">Amyu ♡</span>
          </h1>
        </div>

        <Stepper step={step} />

        <div className="bg-white/85 backdrop-blur-md rounded-[28px] p-6 md:p-8">
          {step === 1 && (
            <div data-testid="step-products">
              <h2 className="font-display text-2xl mb-4">Choisis tes gourmandises</h2>
              <div className="space-y-3">
                {products.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`product-row-${p.id}`}
                    className={`flex items-center gap-4 p-3 rounded-2xl ${p.in_stock ? "bg-amyu-cream" : "bg-amyu-cream/40 opacity-60"}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-sm text-coral">{eur(p.price)}</div>
                      {!p.in_stock && <div className="text-[10px] uppercase text-amyu-muted mt-0.5">Rupture</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        disabled={!p.in_stock || !(qty[p.id] > 0)}
                        onClick={() => setQuantity(p.id, -1)}
                        data-testid={`qty-minus-${p.id}`}
                        className="w-9 h-9 rounded-full bg-white flex items-center justify-center disabled:opacity-30"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-6 text-center font-medium" data-testid={`qty-value-${p.id}`}>
                        {qty[p.id] || 0}
                      </span>
                      <button
                        disabled={!p.in_stock}
                        onClick={() => setQuantity(p.id, +1)}
                        data-testid={`qty-plus-${p.id}`}
                        className="w-9 h-9 rounded-full bg-coral text-white flex items-center justify-center disabled:opacity-30"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-date">
              <h2 className="font-display text-2xl mb-4">Choisis ta date de retrait</h2>
              {availableDates.length === 0 ? (
                <p className="text-amyu-muted">Aucune date disponible pour l&apos;instant.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {availableDates.map((d) => (
                    <button
                      key={d.iso}
                      onClick={() => setDate(d.iso)}
                      data-testid={`date-option-${d.iso}`}
                      className={`p-3 rounded-2xl text-sm font-medium capitalize transition-all ${
                        date === d.iso
                          ? "bg-coral text-white shadow-[0_6px_16px_rgba(229,91,91,0.3)]"
                          : "bg-amyu-cream hover:bg-amyu-pink"
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-slot">
              <h2 className="font-display text-2xl mb-4">Choisis ton créneau</h2>
              {(() => {
                const wd = date ? (new Date(date).getDay() + 6) % 7 : null;
                const daySlots = availability.slots_by_weekday?.[String(wd)] || availability.slots;
                return (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {daySlots.map((s) => (
                      <button
                        key={s}
                        onClick={() => setSlot(s)}
                        data-testid={`slot-option-${s}`}
                        className={`py-3 rounded-2xl text-sm font-medium transition-all ${
                          slot === s ? "bg-coral text-white shadow-[0_6px_16px_rgba(229,91,91,0.3)]" : "bg-amyu-cream hover:bg-amyu-pink"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="step-info">
              <h2 className="font-display text-2xl">Tes coordonnées</h2>
              {[
                { key: "name", label: "Prénom & nom *", type: "text", ph: "Amy Test" },
                { key: "email", label: "Email *", type: "email", ph: "amy@exemple.fr" },
                { key: "phone", label: "Téléphone", type: "tel", ph: "06 12 34 56 78" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="text-xs uppercase tracking-[0.16em] text-amyu-muted">{f.label}</label>
                  <input
                    type={f.type}
                    value={info[f.key]}
                    onChange={(e) => setInfo((s) => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={f.ph}
                    data-testid={`info-${f.key}`}
                    className="mt-1.5 w-full bg-amyu-cream rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-coral/30"
                  />
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-amyu-muted">Note (optionnel)</label>
                <textarea
                  rows={3}
                  value={info.notes}
                  onChange={(e) => setInfo((s) => ({ ...s, notes: e.target.value }))}
                  placeholder="Allergie, occasion spéciale…"
                  data-testid="info-notes"
                  className="mt-1.5 w-full bg-amyu-cream rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-coral/30 resize-none"
                />
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="step-summary">
              <h2 className="font-display text-2xl mb-4">Ta commande ♡</h2>
              <div className="bg-amyu-cream rounded-2xl p-4 space-y-1.5 text-sm">
                {items.map((it) => (
                  <div key={it.id} className="flex justify-between">
                    <span>{it.quantity} × {it.name}</span>
                    <span className="font-medium">{eur(it.line_total)}</span>
                  </div>
                ))}
                <div className="border-t border-dashed border-coral/30 pt-2 mt-2 flex justify-between font-display text-lg">
                  <span>Total</span>
                  <span className="text-coral">{eur(total)}</span>
                </div>
              </div>
              <div className="mt-4 grid gap-2 text-sm">
                <div className="bg-white/60 rounded-2xl p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted">Retrait</div>
                  <div className="font-medium capitalize mt-0.5">
                    {new Date(date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {slot}
                  </div>
                  <div className="text-xs text-amyu-muted mt-0.5">{availability.pickup_location}</div>
                </div>
                <div className="bg-white/60 rounded-2xl p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted">Client</div>
                  <div className="font-medium mt-0.5">{info.name}</div>
                  <div className="text-xs text-amyu-muted">{info.email}{info.phone && ` · ${info.phone}`}</div>
                  {info.notes && <div className="text-xs italic mt-1">« {info.notes} »</div>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:flex mt-6 justify-between">
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            data-testid="step-back"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white disabled:opacity-40"
          >
            <ArrowLeft size={14} /> Retour
          </button>
          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canNext()}
              data-testid="step-next"
              className="btn-coral inline-flex items-center gap-2 disabled:opacity-40"
            >
              Continuer {total > 0 && `— ${eur(total)}`} <ArrowRight size={14} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="submit-order"
              className="btn-coral inline-flex items-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Confirmer ma commande
            </button>
          )}
        </div>
      </main>

      {/* Sticky mobile bar with live recap */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-[60] px-3 pt-2 pb-3 bg-white/95 backdrop-blur-md border-t border-amyu/40 shadow-[0_-8px_24px_rgba(229,91,91,0.15)]">
        {/* Live recap chips — only appear as user progresses */}
        {(items.length > 0 || date || slot) && (
          <div
            data-testid="mobile-recap"
            className="flex items-center gap-1.5 mb-2 overflow-x-auto no-scrollbar text-[11px]"
          >
            {items.length > 0 && (
              <span className="inline-flex items-center gap-1 shrink-0 px-2.5 py-1 rounded-full bg-amyu-cream text-amyu-ink font-medium">
                <ShoppingBag size={11} className="text-coral" />
                {items.reduce((n, it) => n + it.quantity, 0)} article{items.reduce((n, it) => n + it.quantity, 0) > 1 ? "s" : ""}
              </span>
            )}
            {date && (
              <span className="inline-flex items-center gap-1 shrink-0 px-2.5 py-1 rounded-full bg-amyu-pistachio/40 text-amyu-ink font-medium capitalize">
                <Calendar size={11} className="text-coral" />
                {new Date(date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
              </span>
            )}
            {slot && (
              <span className="inline-flex items-center gap-1 shrink-0 px-2.5 py-1 rounded-full bg-amyu-pink/60 text-amyu-ink font-medium">
                <Clock size={11} className="text-coral" />
                {slot}
              </span>
            )}
            {total > 0 && (
              <span className="inline-flex items-center gap-1 shrink-0 ml-auto px-2.5 py-1 rounded-full bg-coral/10 text-coral font-semibold">
                {eur(total)}
              </span>
            )}
          </div>
        )}
        <div className="flex gap-2">
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              className="px-4 py-3 rounded-full bg-amyu-cream"
              data-testid="mobile-step-back"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canNext()}
              data-testid="mobile-step-next"
              className="flex-1 btn-coral disabled:opacity-40 flex items-center justify-center gap-2"
            >
              Continuer {total > 0 && `— ${eur(total)}`}
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="mobile-submit-order"
              className="flex-1 btn-coral disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : "Confirmer ♡"}
            </button>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
